import { Text } from '@chakra-ui/react'
import type { ReactNode } from 'react'

import { ConfirmDialog } from '@mangovoice/mango-ui'

import { useScheduleI18n } from '../../i18n'

export interface DeleteConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title?: string
  description?: ReactNode
  itemName?: string
  cancelLabel?: string
  confirmLabel?: string
  confirmTone?: 'danger' | 'primary'
  isSubmitting?: boolean
}

export const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  itemName,
  cancelLabel,
  confirmLabel,
  confirmTone = 'danger',
  isSubmitting = false,
}: DeleteConfirmationModalProps) => {
  const { text } = useScheduleI18n()
  const resolvedTitle =
    title ??
    text('schedule.modal.deleteConfirmation.defaultTitle', 'Delete item')
  const resolvedCancelLabel =
    cancelLabel ?? text('schedule.modal.deleteConfirmation.cancel', 'Cancel')
  const resolvedConfirmLabel =
    confirmLabel ?? text('schedule.modal.deleteConfirmation.confirm', 'Delete')

  const resolvedDescription =
    typeof description === 'string' ? description : undefined

  const defaultBodyChildren = itemName ? (
    <Text>
      {text(
        'schedule.modal.deleteConfirmation.defaultBodyWithItemPrefix',
        'Are you sure you want to delete ',
      )}
      <strong>{itemName}</strong>
      {text(
        'schedule.modal.deleteConfirmation.defaultBodyWithItemSuffix',
        '? This action cannot be undone.',
      )}
    </Text>
  ) : (
    <Text>
      {text(
        'schedule.modal.deleteConfirmation.defaultBodyWithoutItem',
        'Are you sure you want to delete this item? This action cannot be undone.',
      )}
    </Text>
  )

  const bodyChildren =
    typeof description !== 'string' && description
      ? description
      : !resolvedDescription
        ? defaultBodyChildren
        : undefined

  return (
    <ConfirmDialog
      open={isOpen}
      onClose={onClose}
      title={resolvedTitle}
      description={resolvedDescription}
      confirmLabel={resolvedConfirmLabel}
      cancelLabel={resolvedCancelLabel}
      onConfirm={onConfirm}
      variant={confirmTone === 'danger' ? 'destructive' : 'default'}
      isLoading={isSubmitting}
    >
      {bodyChildren}
    </ConfirmDialog>
  )
}
